import { useEffect, useRef } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

export type BylinePageDocumentOutcome =
	| 'available'
	| 'missing'
	| 'unsupported'
	| 'malformedJson'
	| 'invalidDocument';

export type BylinePageDocumentObservationOptions = {
	contentId?: string;
	outcome: BylinePageDocumentOutcome;
	enabled: boolean;
};

export function useBylinePageDocumentObservation({
	contentId,
	outcome,
	enabled,
}: BylinePageDocumentObservationOptions): void {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const lastReported = useRef<{ contentId: string; outcome: BylinePageDocumentOutcome } | null>(
		null,
	);

	useEffect(() => {
		if (!enabled || !contentId) return;
		if (lastReported.current?.contentId === contentId && lastReported.current.outcome === outcome)
			return;
		// Record before dispatch so a reporting failure cannot trigger another event on rerender.
		lastReported.current = { contentId, outcome };
		try {
			createEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'observed',
					actionSubject: 'bylinePageDocument',
					source: 'byline-connections',
					attributes: { outcome },
				},
			}).fire();
		} catch {
			// Observation must not affect document availability.
		}
	}, [contentId, createEvent, enabled, outcome]);
}
